import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, Loader2, KeyRound, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sec } from "@/lib/securityApi";
import { useST } from "@/i18n/securityText";

/** Second sign-in step: 6-digit authenticator code, or a backup code. onDone(access_token, user). */
export const TwoFactorForm = ({ challenge, onDone, onBack, tone = "blue" }) => {
  const { st } = useST();
  const [backup, setBackup] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, [backup]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    setBusy(true); setError("");
    try {
      const r = await sec.login2fa(challenge, code.trim());
      onDone(r.access_token, r.user);
    } catch (err) {
      setError(err?.response?.data?.detail || st("wrongCode"));
      setCode("");
    } finally { setBusy(false); }
  };

  const btn = tone === "green" ? "bg-venka-green-light text-slate-950 hover:bg-venka-green-light rounded-full" : "bg-venka-blue text-white hover:bg-venka-blue-dark rounded-xl";
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="twofactor-form">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-venka-blue/15 text-venka-blue"><ShieldCheck className="h-5 w-5" /></span>
        <div>
          <p className="font-semibold text-slate-100">{st("tfaTitle")}</p>
          <p className="text-xs text-slate-400">{backup ? st("backupPrompt") : st("tfaPrompt")}</p>
        </div>
      </div>
      <Input ref={ref} value={code} autoComplete="one-time-code" data-testid="twofactor-code"
        inputMode={backup ? "text" : "numeric"} maxLength={backup ? 12 : 7}
        onChange={(e) => { const v = backup ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""); setCode(v); if (!backup && v.length === 6) setTimeout(() => document.getElementById("tfa-submit")?.click(), 0); }}
        placeholder={backup ? "XXXX-XXXX" : "123456"}
        className="h-12 text-center font-mono text-xl tracking-[0.4em] bg-slate-950 border-slate-700" />
      {error && <p className="text-sm text-red-400" data-testid="twofactor-error">{error}</p>}
      <Button id="tfa-submit" type="submit" disabled={busy || !code.trim()} className={`w-full h-11 font-semibold ${btn}`} data-testid="twofactor-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : st("verify")}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button type="button" onClick={() => { setBackup(!backup); setCode(""); setError(""); }} className="inline-flex items-center gap-1 text-venka-blue hover:underline" data-testid="twofactor-toggle">
          <KeyRound className="h-3.5 w-3.5" />{backup ? st("useApp") : st("useBackup")}
        </button>
        {onBack && <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-slate-400 hover:text-slate-200"><ArrowLeft className="h-3.5 w-3.5" />{st("back")}</button>}
      </div>
    </form>
  );
};

export default TwoFactorForm;
